/**
 * Modo Lista | Quadro dentro de cada menu. O quadro usa os mesmos filtros da
 * página; a forma (colunas por, nomes, cores, ocultas, limites, raias) é
 * preferência de cada pessoa por menu, com padrão definido pelo administrador.
 */
import { FatalDate, type RelatedFatal } from "./kit";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { navigate } from "@/lib/router";
import { fmtDate } from "@/lib/format";
import { useCatalog, useLookups } from "@/lib/catalog";
import { confirmar } from "./feedback";
import { Avatar } from "./icons";
import { ErrorBox, Field, FormGrid, Loading, Modal, useAct } from "./kit";

export type BoardMenu = "atividades" | "processos" | "casos" | "comunicacoes";

export interface Layout {
  groupBy: "field" | "date" | "manual";
  groupField: string | null;
  swimlane: "responsibleUserId" | "areaId" | "priority" | null;
  config: {
    mode?: "lista" | "quadro";
    labels?: Record<string, string>; colors?: Record<string, string>; hidden?: string[]; order?: string[];
    wip?: Record<string, number>; dateField?: "internalDueAt" | "plannedAt" | "fatalDueAt"; buckets?: string[];
    columns?: Array<{ id: string; label: string; color?: string }>;
  };
}
interface Col { id: string; label: string; color: string; wip: number | null; locked?: boolean }
interface Card {
  id: string; title: string; subtitle: string | null; lines: string[]; link: string; responsibleUserId: string | null; areaId: string | null;
  priority: string | null; typeId?: string | null; internalDueAt?: string | null; plannedAt?: string | null; fatalDueAt?: string | null;
  relatedFatal?: RelatedFatal | null;
  state?: string;
  paused?: boolean;
  overdue: boolean; since: string | null; column: string;
}
interface Meta {
  layout: Layout; personalized: boolean; office: Layout; canPublish: boolean;
  groupFields: Array<{ field: string; label: string }>; dateBuckets: Col[]; allColumns: Col[]; activityStates: Col[];
}

/**
 * Linha de etapas de um processo/caso: onde está, o que já passou e há
 * quanto tempo. Clicar numa etapa move (com confirmação).
 */
export function StageStepper({ scope, stageId, since, canEdit, onMove }: {
  scope: "processo" | "caso"; stageId: string | null; since: string | null; canEdit: boolean; onMove: (id: string) => void;
}): JSX.Element | null {
  const { data: cat } = useCatalog();
  const stages = (cat?.stages ?? []).filter((s) => s.scope === scope && (s.active || s.id === stageId));
  if (!stages.length) return null;
  const idx = stages.findIndex((s) => s.id === stageId);
  const days = since ? Math.floor((Date.now() - new Date(since).getTime()) / 86_400_000) : null;
  return (
    <div className="stepper">
      {stages.map((s, i) => (
        <span key={s.id} style={{ display: "contents" }}>
          {i > 0 && <span className={`stepbar ${i <= idx ? "done" : ""}`} />}
          <button type="button" className={`step ${i < idx ? "done" : i === idx ? "now" : ""} ${canEdit && i !== idx ? "clickable" : ""}`}
            title={canEdit && i !== idx ? `Mover para "${s.name}"` : undefined}
            onClick={async () => { if (canEdit && i !== idx && await confirmar(`Mover para a etapa "${s.name}"? Fica registrado na trilha.`, { confirm: "Mover" })) onMove(s.id); }}>
            <span className="c">{i < idx ? "✓" : i + 1}</span>{s.name}
            {i === idx && days !== null && <span className="badge muted" style={{ marginLeft: 4 }}>há {days} dia(s)</span>}
          </button>
        </span>
      ))}
      {idx < 0 && <span className="muted small" style={{ marginLeft: 12 }}>sem etapa definida</span>}
    </div>
  );
}

const isSmall = () => typeof window !== "undefined" && window.innerWidth < 800;

/** Preferência do menu (modo + forma do quadro). */
export function useBoardPref(menu: BoardMenu) {
  const qc = useQueryClient();
  const key = ["quadro-pref", menu];
  const { data: meta } = useQuery({ queryKey: key, queryFn: () => api.get<Meta>(`/quadro/${menu}`), staleTime: 60_000 });
  const save = useAct((l: Layout) => api.put(`/quadro/${menu}`, l), [key]);
  // No celular, sempre lista.
  const mode: "lista" | "quadro" = isSmall() ? "lista" : meta?.layout.config.mode ?? "lista";
  const setMode = (m: "lista" | "quadro") => {
    if (!meta) return;
    const l = { ...meta.layout, config: { ...meta.layout.config, mode: m } };
    qc.setQueryData(key, { ...meta, layout: l }); // resposta imediata
    save.mutate(l);
  };
  return { meta, mode, setMode, save };
}

export function ModeSwitch({ mode, onChange }: { mode: "lista" | "quadro"; onChange: (m: "lista" | "quadro") => void }): JSX.Element | null {
  if (isSmall()) return null;
  return (
    <div className="seg" title="Como ver esta tela">
      <button className={mode === "lista" ? "active" : ""} onClick={() => onChange("lista")}>☰ Lista</button>
      <button className={mode === "quadro" ? "active" : ""} onClick={() => onChange("quadro")}>▦ Quadro</button>
    </div>
  );
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** O quadro do menu, com os filtros da página. */
export function MenuBoard({ menu, filters, meta }: { menu: BoardMenu; filters: Record<string, unknown>; meta: Meta }): JSX.Element {
  const { userName, areaName, typeOf } = useLookups();
  const layout = meta.layout;
  const [editing, setEditing] = useState(false);
  const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== undefined && v !== null && v !== false));
  const key = ["quadro-dados", menu, layout, cleanFilters];
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: key,
    queryFn: () => api.post<{ columns: Array<Col & { count: number; items: Card[] }>; total: number }>(`/quadro/${menu}/dados`, { layout, filters: cleanFilters }),
    refetchInterval: 60_000,
  });
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const move = useAct(
    (v: { entityId: string; columnId: string; position?: number }) =>
      api.post<{ ok?: true; action?: string; entityId?: string }>(`/quadro/${menu}/mover`, { layout, ...v }),
    [["quadro-dados", menu], ["painel"], ["atividades"], ["processos"], ["casos"], ["comunicacoes"]],
    (r) => {
      if (!r.action) return;
      const back = encodeURIComponent(window.location.pathname + window.location.search);
      if (r.action === "triagem") navigate(`/controladoria?com=${r.entityId}`);
      else navigate(`/atividades/${r.entityId}?acao=${r.action}&voltar=${back}`);
    },
  );
  const groupLabel = layout.groupBy === "date" ? "Data" : layout.groupBy === "manual" ? "Minhas colunas"
    : meta.groupFields.find((g) => g.field === layout.groupField)?.label ?? "";

  const laneOf = (c: Card): string => {
    if (layout.swimlane === "responsibleUserId") return c.responsibleUserId ?? "";
    if (layout.swimlane === "areaId") return c.areaId ?? "";
    if (layout.swimlane === "priority") return c.priority ?? "";
    return "_";
  };
  const laneLabel = (k: string) => layout.swimlane === "responsibleUserId" ? (k ? userName(k) : "Sem responsável")
    : layout.swimlane === "areaId" ? (k ? areaName(k) : "Sem área")
    : layout.swimlane === "priority" ? (k || "Sem prioridade") : "";

  const drop = (colId: string, index: number) => {
    if (drag) move.mutate({ entityId: drag, columnId: colId, position: index });
    setDrag(null); setOver(null);
  };

  const card = (it: Card, i: number, colId: string) => {
    const d = daysSince(it.since);
    const typeName = it.typeId ? typeOf(it.typeId)?.name : null;
    return (
      <div key={it.id} className={`kb-card ${it.overdue ? "kb-late" : ""} ${it.state === "aguardando_dependencia" ? "waiting" : ""} ${it.paused ? "paused" : ""} ${it.state === "cancelada" ? "kb-cancelled" : ""}`} title={it.state === "aguardando_dependencia" ? "Aguardando etapa anterior: ainda não pode ser feita" : undefined} draggable
        onDragStart={(e) => { setDrag(it.id); e.dataTransfer.effectAllowed = "move"; }}
        onDragEnd={() => { setDrag(null); setOver(null); }}
        onDragOver={(e) => { if (layout.groupBy === "manual") { e.preventDefault(); e.stopPropagation(); setOver(colId); } }}
        onDrop={(e) => { if (layout.groupBy === "manual") { e.preventDefault(); e.stopPropagation(); drop(colId, i); } }}
        onClick={() => navigate(it.link)}>
        {it.subtitle && <div className="mono small muted">{it.subtitle}</div>}
        <div className="kb-title">{it.paused && <span className="kb-paused" title="Já foi iniciada e está pausada">⏸ </span>}{it.state === "cancelada" && <span className="muted" title="Cancelada: encerrada sem ser feita">✕ </span>}{it.title}</div>
        {typeName && <div className="muted small">{typeName}</div>}
        {it.lines.filter(Boolean).map((l, j) => <div key={j} className="muted small">{l}</div>)}
        <div className="row between small" style={{ marginTop: 4 }}>
          <span className="row nowrap" style={{ gap: 6 }}>{it.responsibleUserId && <><Avatar name={userName(it.responsibleUserId)} id={it.responsibleUserId} size={20} /><span className="muted">{userName(it.responsibleUserId).split(" ")[0]}</span></>}</span>
          <span className="row" style={{ gap: 6 }}>
            {it.fatalDueAt ? <span className="text-bad" title="Prazo fatal">⚑ {fmtDate(it.fatalDueAt).slice(0, 5)}</span>
              : it.relatedFatal && <FatalDate related={it.relatedFatal} short />}
            {it.internalDueAt && <span className={it.overdue ? "text-bad" : "muted"} title="Prazo interno">{fmtDate(it.internalDueAt).slice(0, 5)}</span>}
            {menu !== "atividades" && d !== null && <span className={d > 60 ? "text-bad" : d > 30 ? "text-warn" : "muted"} title="Dias nesta coluna">{d}d</span>}
          </span>
        </div>
      </div>
    );
  };

  const cols = data?.columns ?? [];
  const lanes = layout.swimlane ? [...new Set(cols.flatMap((c) => c.items.map(laneOf)))].sort((a, b) => laneLabel(a).localeCompare(laneLabel(b))) : ["_"];
  const truncated = cols.some((c) => c.count > c.items.length);

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row">
        <span className="muted small">Colunas por <strong>{groupLabel}</strong>{meta.personalized ? " · personalizado por você" : " · padrão do escritório"}</span>
        <span className="spacer" />
        {move.isPending && <span className="muted small">Salvando…</span>}
        <button className="btn small" onClick={() => void refetch()}>Atualizar</button>
        <button className="btn small" onClick={() => setEditing(true)}>Personalizar quadro</button>
      </div>
      <ErrorBox error={error ?? move.error} />
      {truncated && <div className="muted small">Algumas colunas têm mais itens do que o quadro mostra — use os filtros acima.</div>}
      {isLoading && <Loading />}
      {data && lanes.map((lane) => (
        <div key={lane}>
          {layout.swimlane && <div className="lane-head">{laneLabel(lane)}</div>}
          <div className="kanban">
            {cols.map((c) => {
              const items = c.items.filter((it) => laneOf(it) === lane);
              const count = layout.swimlane ? items.length : c.count;
              const overWip = c.wip !== null && count > c.wip;
              return (
                <div key={c.id} className={`kb-col ${over === c.id && drag ? "kb-over" : ""} ${overWip ? "kb-wip" : ""}`}
                  onDragOver={(e) => { if (drag && !c.locked) { e.preventDefault(); setOver(c.id); } }}
                  onDragLeave={() => setOver(null)}
                  onDrop={(e) => { e.preventDefault(); if (!c.locked) drop(c.id, items.length); }}>
                  <div className="kb-head" style={{ ["--kb-color" as string]: c.color }}>
                    <span>{c.label}{c.locked && <span className="muted small" title="Preenchida automaticamente"> · auto</span>}</span>
                    <span className="kb-count" title={c.wip ? `Limite: ${c.wip}` : undefined}>{count}{c.wip ? `/${c.wip}` : ""}</span>
                  </div>
                  <div className="kb-body">
                    {items.map((it, i) => card(it, i, c.id))}
                    {!items.length && <div className="muted small pad" style={{ textAlign: "center" }}>—</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div className="muted small">
        {layout.groupBy === "field" && "Arrastar muda o item de verdade e fica registrado. Soltar em “Feito” abre a conclusão, com as regras de sempre."}
        {layout.groupBy === "date" && "Arrastar muda o prazo interno (ou a data planejada). O prazo fatal nunca muda arrastando."}
        {layout.groupBy === "manual" && "Suas colunas: arrastar só organiza o seu quadro, não altera o item."}
      </div>
      {editing && <BoardCustomizer menu={menu} meta={meta} onClose={() => setEditing(false)} />}
    </div>
  );
}

// ------------------------------------------------------------ personalizar

function BoardCustomizer({ menu, meta, onClose }: { menu: BoardMenu; meta: Meta; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [l, setL] = useState<Layout>(JSON.parse(JSON.stringify(meta.layout)) as Layout);
  const [cols, setCols] = useState<Col[]>(meta.allColumns);
  const cfg = l.config;
  const upC = (p: Partial<Layout["config"]>) => setL({ ...l, config: { ...l.config, ...p } });

  // Trocou "colunas por": busca as colunas possíveis do novo agrupamento.
  useEffect(() => {
    if (l.groupBy === "manual") { setCols([]); return; }
    if (l.groupBy === "date") { setCols(meta.dateBuckets); return; }
    void api.post<Col[]>(`/quadro/${menu}/colunas`, { groupBy: l.groupBy, groupField: l.groupField, config: {} }).then(setCols).catch(() => setCols([]));
  }, [l.groupBy, l.groupField]); // eslint-disable-line react-hooks/exhaustive-deps

  const hidden = new Set(cfg.hidden ?? []);
  const order = cfg.order ?? [];
  const sorted = [...cols].sort((a, b) => {
    const ia = order.indexOf(a.id); const ib = order.indexOf(b.id);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });
  const moveCol = (i: number, d: -1 | 1) => {
    const ids = sorted.map((c) => c.id); const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    upC({ order: ids });
  };
  const manual = cfg.columns ?? [];
  const inv = [["quadro-pref", menu], ["quadro-dados", menu]];
  const payload = (): Layout => ({
    ...l, groupField: l.groupBy === "field" ? l.groupField : null,
    config: { ...cfg, ...(l.groupBy === "date" && !cfg.buckets?.length ? { buckets: meta.dateBuckets.map((b) => b.id) } : {}) },
  });
  const save = useAct(() => api.put(`/quadro/${menu}`, payload()), inv, onClose);
  const publish = useAct(() => api.put(`/quadro/${menu}/padrao`, payload()), inv, onClose);
  const reset = useAct(() => api.del(`/quadro/${menu}`), inv, () => { void qc.invalidateQueries(); onClose(); });

  return (
    <Modal title="Personalizar quadro" onClose={onClose} wide footer={
      <>
        {meta.personalized && <button className="btn" onClick={() => reset.mutate()}>Voltar ao padrão do escritório</button>}
        <span className="spacer" />
        {meta.canPublish && l.groupBy !== "manual" && <button className="btn" onClick={async () => { if (await confirmar("Usar esta forma como padrão para todo o escritório? Quem já personalizou mantém a sua.", { confirm: "Definir padrão" })) publish.mutate(); }}>Definir como padrão do escritório</button>}
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={save.isPending || (l.groupBy === "manual" && !manual.length)} onClick={() => save.mutate()}>Salvar para mim</button>
      </>
    }>
      <div className="stack">
        <FormGrid cols={3}>
          <Field label="Colunas por">
            <select className="input" value={l.groupBy === "field" ? `field:${l.groupField}` : l.groupBy} onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith("field:")) setL({ ...l, groupBy: "field", groupField: v.slice(6), config: { mode: cfg.mode } });
              else setL({ ...l, groupBy: v as Layout["groupBy"], groupField: null, config: { mode: cfg.mode, ...(v === "manual" ? { columns: [{ id: "entrada", label: "Entrada" }, { id: "prioridade", label: "Prioridade" }] } : {}) } });
            }}>
              {meta.groupFields.map((g) => <option key={g.field} value={`field:${g.field}`}>{g.label}</option>)}
              {menu === "atividades" && <option value="date">Data (atrasadas, hoje, amanhã, semana…)</option>}
              <option value="manual">Minhas colunas (só organiza)</option>
            </select>
          </Field>
          {l.groupBy === "date" && (
            <Field label="Data usada">
              <select className="input" value={cfg.dateField ?? "internalDueAt"} onChange={(e) => upC({ dateField: e.target.value as Layout["config"]["dateField"] })}>
                <option value="internalDueAt">Prazo interno</option><option value="plannedAt">Data planejada</option><option value="fatalDueAt">Prazo fatal (só visualiza)</option>
              </select>
            </Field>
          )}
          <Field label="Linhas (raias)">
            <select className="input" value={l.swimlane ?? ""} onChange={(e) => setL({ ...l, swimlane: (e.target.value || null) as Layout["swimlane"] })}>
              <option value="">Nenhuma</option><option value="responsibleUserId">Por responsável</option><option value="areaId">Por área</option>
              {menu === "atividades" && <option value="priority">Por prioridade</option>}
            </select>
          </Field>
        </FormGrid>

        <div className="fld-label">Colunas</div>
        {l.groupBy === "manual" ? (
          <div className="stack">
            {manual.map((c, i) => (
              <div key={c.id} className="row nowrap">
                <input type="color" className="input" style={{ width: 44, height: 34, padding: 2 }} value={c.color ?? "#34506F"} onChange={(e) => upC({ columns: manual.map((x, j) => (j === i ? { ...x, color: e.target.value } : x)) })} />
                <input className="input" value={c.label} onChange={(e) => upC({ columns: manual.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <button className="btn small" disabled={i === 0} onClick={() => { const n = [...manual]; [n[i - 1], n[i]] = [n[i]!, n[i - 1]!]; upC({ columns: n }); }}>↑</button>
                <button className="btn small" disabled={i === manual.length - 1} onClick={() => { const n = [...manual]; [n[i + 1], n[i]] = [n[i]!, n[i + 1]!]; upC({ columns: n }); }}>↓</button>
                <button className="btn small danger" onClick={() => upC({ columns: manual.filter((_, j) => j !== i) })}>Remover</button>
              </div>
            ))}
            <div><button className="btn small" onClick={() => upC({ columns: [...manual, { id: `c${Date.now().toString(36)}`, label: "Nova coluna", color: "#34506F" }] })}>+ coluna</button></div>
            <div className="muted small">Itens ainda não organizados entram na primeira coluna.</div>
          </div>
        ) : (
          <table className="table">
            <thead><tr><th style={{ width: 70 }}>Ordem</th><th style={{ width: 60 }}>Mostrar</th><th>Nome no seu quadro</th><th style={{ width: 60 }}>Cor</th><th style={{ width: 110 }}>Limite</th></tr></thead>
            <tbody>
              {sorted.map((c, i) => (
                <tr key={c.id} className={hidden.has(c.id) ? "row-muted" : ""}>
                  <td><div className="row nowrap"><button className="btn small" disabled={i === 0} onClick={() => moveCol(i, -1)}>↑</button><button className="btn small" disabled={i === sorted.length - 1} onClick={() => moveCol(i, 1)}>↓</button></div></td>
                  <td><input type="checkbox" checked={!hidden.has(c.id)} onChange={() => { const h = new Set(hidden); if (h.has(c.id)) h.delete(c.id); else h.add(c.id); upC({ hidden: [...h] }); }} /></td>
                  <td><input className="input" value={cfg.labels?.[c.id] ?? ""} placeholder={c.label} onChange={(e) => upC({ labels: { ...cfg.labels, [c.id]: e.target.value } })} /></td>
                  <td><input type="color" className="input" style={{ height: 32, padding: 2 }} value={cfg.colors?.[c.id] ?? c.color} onChange={(e) => upC({ colors: { ...cfg.colors, [c.id]: e.target.value } })} /></td>
                  <td><input type="number" min={1} className="input" value={cfg.wip?.[c.id] ?? ""} placeholder="—" onChange={(e) => {
                    const w = { ...(cfg.wip ?? {}) };
                    if (e.target.value) w[c.id] = Number(e.target.value); else delete w[c.id];
                    upC({ wip: w });
                  }} /></td>
                </tr>
              ))}
              {!sorted.length && <tr><td colSpan={5} className="muted small">Carregando colunas…</td></tr>}
            </tbody>
          </table>
        )}
        {l.groupField === "stageId" && <div className="muted small">Criar, renomear ou remover etapas é do administrador (Configurações → Etapas). Aqui você escolhe como elas aparecem para você.</div>}
        <ErrorBox error={save.error ?? publish.error ?? reset.error} />
      </div>
    </Modal>
  );
}

